// Import the auto-generated client and types
import { client as generatedClient } from './api/client.gen';
import { reportPreviewApiUnreachable } from '../utils/previewWake';
import { addBreadcrumb } from '../utils/sentry';

// Common API configuration
// When VITE_DEV_AUTO_LOGIN is enabled, use '/api' prefix (proxied through Vite dev server)
// This avoids cross-origin cookie issues when running local frontend against remote backend
export const API_BASE_URL =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true'
    ? '/api'
    : import.meta.env.VITE_API_URL || 'http://localhost:8001';

// Configure the generated default client in place instead of creating a
// separate instance. Every generated SDK function falls back to this singleton
// when a call site omits `client`, so an unconfigured default silently fetches
// relative URLs from the frontend origin and receives the SPA fallback HTML
// with HTTP 200. Configuring the singleton itself makes the explicit `client`
// argument redundant belt-and-braces rather than load-bearing.
const customClient = generatedClient;
customClient.setConfig({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let sessionExpiredHandled = false;

const responseInterceptors = customClient.instance?.interceptors?.response;
if (responseInterceptors) {
  responseInterceptors.use(
    response => response,
    error => {
      // No-op outside preview builds; on a preview, lets the wake guard check
      // whether the api was frozen for being idle.
      reportPreviewApiUnreachable(error.response?.status);
      if (error.response?.status === 401 && !sessionExpiredHandled) {
        sessionExpiredHandled = true;
        addBreadcrumb('Session expired; clearing local auth state', 'auth');
        localStorage.removeItem('roger-emr-store');
        void import('../store').then(({ useStore }) => {
          useStore.setState({
            user: null,
            isAuthenticated: false,
            isAuthLoading: false,
            authChecked: true,
            rogerDocFeatureFlags: {},
          });
        });
      }
      return Promise.reject(error);
    }
  );
}

// Export the custom client for direct use
export const client = customClient;

// Re-export commonly used functions with better names for compatibility
export {
  // Auth
  loginAuthLoginGet as login,
  authCallbackAuthCallbackGet as authCallback,
  logoutAuthLogoutGet as logout,
  getCurrentCtxInfoAuthMeGet as getCurrentUser,

  // Intake
  getIntakesEndpointIntakeGet as getIntakes,
  getIntakeEndpointIntakeIntakeIdGet as getIntake,
  updateIntakeEndpointIntakeIntakeIdPut as updateIntake,
  updateIntakeStatusEndpointIntakeIntakeIdStatusPut as updateIntakeStatus,
  rejectIntakeEndpointIntakeIntakeIdRejectPut as rejectIntake,
  uploadFileAndCreateIntakeIntakeUploadPost as uploadFile,
  uploadMultipleFilesAndCreateIntakeIntakeUploadMultiplePost,
  createManualIntakeIntakeManualPost as createManualIntake,
  exportIntakesEndpointIntakeExportGet as exportIntakes,
  getAuthQueueEndpointIntakeAuthQueueGet as getAuthQueue,
  getEpisodeDetailsEndpointIntakeIntakeIdEpisodeDetailsGet as getEpisodeDetails,
  getReferralDocPresignedUrlIntakeIntakeIdReferralDocGet as getReferralDoc,
  getReferralDocsPresignedUrlsIntakeIntakeIdReferralDocsGet as getReferralDocs,
  getIntakeDocumentsNameDictIntakeIntakeIdDocumentsNameDictGet as getIntakeDocumentsNameDict,
  deleteIntakeDocumentIntakeDocumentsDocumentIdDelete as deleteIntakeDocument,
  updateIntakeDocumentIntakeDocumentsDocumentIdPatch as updateIntakeDocument,
  uploadDocumentToIntakeIntakeIntakeIdDocumentsPost as uploadDocumentToIntake,
  stampIntakeDocumentIntakeDocumentsDocumentIdStampPost as stampIntakeDocument,
  scheduleRogerExistingPatientIntakeIntakeIdScheduleRogerExistingPatientPost as scheduleRogerExistingPatient,
  scheduleInKinnserIntakeIntakeIdScheduleInKinnserPost as scheduleInKinnser,
  autoAddInKinnserIntakeIntakeIdAutoAddInKinnserPost as autoAddInKinnser,

  // Patients
  getPatientsPatientsGet as getPatients,
  exportPatientsPatientsExportGet as exportPatients,
  getPatientPatientsPatientIdGet as getPatient,
  createPatientPatientsPost as createPatient,
  updatePatientPatientsPatientIdPut as updatePatient,
  deletePatientPatientsPatientIdDelete as deletePatient,
  getPatientCountPatientsCountGet as getPatientCount,
  getPatientVisitsPatientsPatientIdVisitsGet as getPatientVisits,
  getPatientTasksPatientsPatientIdTasksGet as getPatientTasks,
  getDeletedPatientTasksPatientsPatientIdDeletedTasksGet as getDeletedPatientTasks,
  createPatientVisitPatientsPatientIdVisitsPost as createPatientVisit,
  createOrderOrdersPost as createOrder,
  getPatientDocumentsPatientsPatientIdDocumentsGet as getPatientDocuments,
  getPatientDocumentUrlPatientsPatientIdDocumentsDocumentIdUrlGet as getPatientDocumentUrl,
  uploadPatientDocumentPatientsPatientIdDocumentsPost as uploadPatientDocument,
  updatePatientDocumentPatientsPatientIdDocumentsDocumentIdPatch as updatePatientDocument,
  deletePatientDocumentPatientsPatientIdDocumentsDocumentIdDelete as deletePatientDocument,
  deletePatientIntakeDocumentPatientsPatientIdIntakeDocumentsDocumentIdDelete as deletePatientIntakeDocument,
  getCurrentEpisodePatientsPatientIdCurrentEpisodeGet as getCurrentEpisode,
  updateCurrentEpisodeCaseManagerPatientsPatientIdCurrentEpisodeCaseManagerPatch as updateCurrentEpisodeCaseManager,
  updateCurrentEpisodeClinicalManagerPatientsPatientIdCurrentEpisodeClinicalManagerPatch as updateCurrentEpisodeClinicalManager,
  updateCurrentEpisodeDisasterTriageCodesPatientsPatientIdCurrentEpisodeDisasterTriageCodesPatch,
  listEpisodesForPatient,
  createEpisode,
  takeOverEpisode,
  takeOverEpisodeBasic,
  getEpisodeTakeOverPlan,
  getEpisodeTakeOverUndoCheck,
  undoEpisodeTakeOver,

  // Patient communications (non-visit tasks)
  listPatientCommunicationsPatientsPatientIdCommunicationsGet as listPatientCommunications,
  createPatientCommunicationPatientsPatientIdCommunicationsPost as createPatientCommunication,
  getPatientCommunicationPatientCommunicationsCommunicationIdGet as getPatientCommunication,
  updatePatientCommunicationPatientCommunicationsCommunicationIdPut as updatePatientCommunication,
  deletePatientCommunicationPatientCommunicationsCommunicationIdDelete as deletePatientCommunication,
  getPatientCommunicationPdfPatientCommunicationsCommunicationIdPdfGet as getPatientCommunicationPdf,
  getThirtyDaySummaryPdfPatientCommunicationsCommunicationIdThirtyDaySummaryPdfGet as getThirtyDaySummaryPdf,
  getThirtyDaySummaryPrefillPatientCommunicationsCommunicationIdThirtyDaySummaryPrefillGet as getThirtyDaySummaryPrefill,
  createMiscTaskPatientsPatientIdMiscTasksPost as createMiscTask,
  getMiscTaskMiscTasksMiscTaskIdGet as getMiscTask,
  updateMiscTaskMiscTasksMiscTaskIdPatch as updateMiscTask,
  deleteMiscTaskMiscTasksMiscTaskIdDelete as deleteMiscTask,

  // Visits
  getVisitVisitsVisitIdGet as getVisit,
  createVisitVisitsPost as createVisit,
  updateVisitVisitsVisitIdPut as updateVisit,
  deleteVisitVisitsVisitIdDelete as deleteVisit,
  getVisitDataVisitsVisitIdDataGet as getVisitData,
  getVisitExternalAnswersVisitsVisitIdExternalAnswersGet as getVisitExternalAnswers,
  updateVisitDataVisitsVisitIdDataPut as updateVisitData,
  getVisitWorkLogVisitsVisitIdWorkLogGet as getVisitWorkLog,
  getVisitEvvReadingsVisitsVisitIdEvvGet as getVisitEvvReadings,
  getVisitEvvCaptureReadingsVisitsVisitIdEvvCaptureViewGet as getVisitEvvCaptureReadings,
  getVisitsByClinicianVisitsByClinicianClinicianIdGet as getVisitsByClinician,
  getVisitPdfVisitsVisitIdPdfGet as getVisitPdf,
  getVisitPdfRevisionVisitsVisitIdPdfRevisionGet as getVisitPdfRevision,
  getOrderWorkLogOrdersOrderIdWorkLogGet as getOrderWorkLog,
  getOrderPdfRevisionOrdersOrderIdPdfRevisionGet as getOrderPdfRevision,
  getPatientCommunicationWorkLogPatientCommunicationsCommunicationIdWorkLogGet as getPatientCommunicationWorkLog,
  getPatientCommunicationPdfRevisionPatientCommunicationsCommunicationIdPdfRevisionGet as getPatientCommunicationPdfRevision,
  getMiscTaskWorkLogMiscTasksMiscTaskIdWorkLogGet as getMiscTaskWorkLog,
  listOasisManagerVisitsVisitsOasisManagerGet as listOasisManagerVisits,
  getOasisExportReadinessVisitsOasisManagerExportReadinessPost as getOasisExportReadiness,

  // Payroll Manager (EMR-517)
  listPayrollVisitsPayrollVisitsGet as listPayrollVisits,
  exportPayrollVisitsPayrollVisitsExportGet as exportPayrollVisits,
  createPayrollEntryPayrollEntriesPost as createPayrollEntry,
  exportAllPayrollEntriesPayrollEntriesExportGet as exportAllPayrollEntries,
  listPayrollEntriesPayrollEntriesGet as listPayrollEntries,
  listPayrollEntryVisitsPayrollEntriesPayrollEntryIdVisitsGet as listPayrollEntryVisits,
  exportPayrollEntryVisitsPayrollEntriesPayrollEntryIdExportGet as exportPayrollEntryVisits,
  deletePayrollEntryPayrollEntriesPayrollEntryIdDelete as deletePayrollEntry,
  removePayrollEntryVisitPayrollEntriesPayrollEntryIdVisitsVisitIdDelete as removePayrollEntryVisit,
  // Payroll PDF (EMR-3184)
  printPayrollVisitsPayrollVisitsPrintGet as printPayrollVisits,
  printPayrollEntryVisitsPayrollEntriesPayrollEntryIdPrintGet as printPayrollEntryVisits,
  // Which visit statuses reach Payroll Manager (EMR-2520)
  getPayrollSettingsPayrollSettingsGet as getPayrollSettings,
  updatePayrollSettingsPayrollSettingsPut as updatePayrollSettings,
  markVisitsExportedVisitsMarkExportedPost as markVisitsExported,
  exportOasisBatchVisitsExportOasisBatchPost as exportOasisBatch,
  previewShpBatchVisitsOasisManagerShpBatchPreviewGet as previewShpBatch,
  downloadShpSentXmlVisitsOasisManagerVisitIdShpSentXmlGet as downloadShpSentXml,
  precheckOasisBatchVisitsExportOasisPrecheckPost as precheckOasisBatch,
  unmarkVisitsExportedVisitsUnmarkExportedPost as unmarkVisitsExported,
  markVisitAsMissedVisitsVisitIdMarkMissedPost as markVisitAsMissed,
  unmarkVisitAsMissedVisitsVisitIdMarkMissedDelete as unmarkVisitAsMissed,
  updateMissedVisitDataVisitsVisitIdMissedDataPatch as updateMissedVisitData,
  getVisitTypeChangeOptionsVisitsVisitIdTypeOptionsGet as getVisitTypeChangeOptions,
  changeVisitTypeVisitsVisitIdTypePatch as changeVisitType,
  setVisitOrderNotRequiredVisitsVisitIdOrderNotRequiredPatch as setVisitOrderNotRequired,

  // Episodes
  getEpisodeEpisodesEpisodeIdGet as getEpisode,
  updateEpisodeEpisodesEpisodeIdPatch as updateEpisode,
  reopenEpisode,
  completeEpisode,
  syncEpisodeDateToVisit,
  getEpisodeWorksheetEpisodesEpisodeIdWorksheetGet as getEpisodeWorksheet,
  updateEpisodeWorksheetEpisodesEpisodeIdWorksheetPut as updateEpisodeWorksheet,
  listEpisodeNotesEpisodesEpisodeIdNotesGet as listEpisodeNotes,
  createEpisodeNoteEpisodesEpisodeIdNotesPost as createEpisodeNote,
  updateEpisodeNoteEpisodesEpisodeIdNotesNoteIdPatch as updateEpisodeNote,
  deleteEpisodeNoteEpisodesEpisodeIdNotesNoteIdDelete as deleteEpisodeNote,
  batchPrintEpisodeTasksEpisodesEpisodeIdTasksBatchPrintPost as batchPrintEpisodeTasks,
  getEpisodeLupa,

  // Task signing and notes (shared for visits and orders)
  signTaskEndpointTasksTaskIdSignPost as signTask,
  approveTaskEndpointTasksTaskIdApprovePost as approveTask,
  breakSignaturesEndpointTasksTaskIdEditPost as breakTaskSignatures,
  bypassTaskSignatureEndpointTasksTaskIdBypassSignaturePost as bypassTaskSignature,
  completeTaskOutsideRecordEndpointTasksTaskIdCompleteOutsideRecordPost as completeTaskOutsideRecord,
  updateTaskOutsideRecordEndpointTasksTaskIdOutsideRecordPatch as updateTaskOutsideRecord,
  getTaskOutsideRecordEndpointTasksTaskIdOutsideRecordGet as getTaskOutsideRecord,
  uploadTaskOutsideRecordDocumentEndpointTasksTaskIdOutsideRecordDocumentsPost as uploadTaskOutsideRecordDocument,
  deleteTaskOutsideRecordDocumentEndpointTasksTaskIdOutsideRecordDocumentsDocumentIdDelete as deleteTaskOutsideRecordDocument,
  listTaskSignatureBypassesTasksTaskIdSignatureBypassesGet as listTaskSignatureBypasses,
  submitTaskWithoutSignatureEndpointTasksTaskIdSubmitWithoutSignaturePost as submitTaskWithoutSignature,
  returnTaskToClinicianEndpointTasksTaskIdReturnToClinicianPost as returnTaskToClinician,
  updateTaskNotesTasksTaskIdNotesPut as updateTaskNotes,
  bulkAppendTaskNotesTasksNotesBulkPost as bulkAppendTaskNotes,

  // Order lifecycle
  checkoutOrderOrdersOrderIdCheckoutPost as checkoutOrder,
  checkinOrderOrdersOrderIdCheckinPost as checkinOrder,
  revertCheckoutOrderOrdersOrderIdRevertCheckoutPost as revertCheckoutOrder,
  revertCheckinOrderOrdersOrderIdRevertCheckinPost as revertCheckinOrder,
  getOrderManagerCountsOrdersManagerCountsGet as getOrderManagerCounts,
  getOrderManagerFilterOptionsOrdersManagerOptionsGet as getOrderManagerFilterOptions,
  getOrderManagerItemsOrdersManagerGet as getOrderManagerItems,
  listCms485OasisVisitsPatientsPatientIdCms485OasisVisitsGet as listCms485OasisVisits,
  getOrderCms485FormOrdersOrderIdCms485FormGet as getOrderCms485Form,
  getOrderCms485PrefillOrdersOrderIdCms485PrefillGet as getOrderCms485Prefill,
  listPhoRocVisitsPatientsPatientIdPhoRocVisitsGet as listPhoRocVisits,
  updateEpisodeAssignmentsEpisodesEpisodeIdAssignmentsPatch as updateEpisodeAssignments,
  listEpisodeUnsentBillingCyclesEpisodesEpisodeIdUnsentBillingCyclesGet as listEpisodeUnsentBillingCycles,
  syncNoaPhysicianEpisodesEpisodeIdSyncNoaPhysicianPost as syncEpisodeNoaPhysician,
  checkNoaPhysicianSyncEpisodesEpisodeIdSyncNoaPhysicianCheckGet as checkEpisodeNoaPhysicianSync,
  checkOasisMrnSyncPatientsPatientIdOasisMrnSyncCheckGet as checkPatientOasisMrnSync,
  syncOasisMrnPatientsPatientIdOasisMrnSyncPost as syncPatientOasisMrn,
  checkPatientOasisExportErrorsPatientsPatientIdOasisExportErrorsPost as checkPatientOasisExportErrors,
  updateOrderOrdersOrderIdPatch as updateOrder,
  deleteOrderOrdersOrderIdDelete as deleteOrder,
  getOrderOrdersOrderIdGet as getOrder,
  getOrderPdfOrdersOrderIdPdfGet as getOrderPdf,

  // Tasks (generic — signatures, documents)
  getTaskSignaturesTasksTaskIdSignaturesGet as getTaskSignatures,

  // Patient Insurances
  listPatientInsurancesPatientsPatientIdInsuranceGet as listPatientInsurances,
  createPatientInsurancePatientsPatientIdInsurancePost as createPatientInsurance,
  getPatientInsurancePatientsPatientIdInsurancePatientInsuranceIdGet as getPatientInsurance,
  updatePatientInsurancePatientsPatientIdInsurancePatientInsuranceIdPut as updatePatientInsurance,
  deletePatientInsurancePatientsPatientIdInsurancePatientInsuranceIdDelete as deletePatientInsurance,

  // Allergies
  createPatientAllergyPatientsPatientIdAllergyPost as createPatientAllergy,
  createUserDefinedPatientAllergyPatientsPatientIdAllergyUserDefinedPost as createUserDefinedPatientAllergy,
  deletePatientAllergyPatientsAllergyPatientAllergyIdDelete as deletePatientAllergy,
  getPatientAllergiesPatientsPatientIdAllergyGet as getPatientAllergies,
  getUserDefinedAllergiesAllergiesUserDefinedGet as getUserDefinedAllergies,
  searchAllergiesAllergiesGet as searchAllergies,

  // Imported (raw Kinnser) allergy lines — a separate endpoint and a separate
  // type from the coded list above, so an uncoded line cannot reach a caller
  // that expects a screened allergen.
  getImportedAllergyProfilePatientsPatientIdAllergyImportedGet as getImportedAllergyProfile,
  reconcileImportedAllergyLine,
  declineImportedAllergyLine,
  carryForwardImportedNoKnownAllergies,
  // A certain match the import coded on its own, and the map's suggestion
  // for a raw line (EMR-2408/2415).
  revertImportedAllergyLine,
  getImportedAllergySuggestion,
  revertImportedMedicationLine,
  getImportedMedicationSuggestion,

  // Payors
  getPayorsEndpointPayorsGet as getPayors,
  getPayorEndpointPayorsPayorIdGet as getPayor,
  createPayorEndpointPayorsPost as createPayor,
  updatePayorEndpointPayorsPayorIdPut as updatePayor,
  deletePayorEndpointPayorsPayorIdDelete as deletePayor,

  // Agency Insurances
  getAgencyInsurancesClinicsInsuranceGet as getAgencyInsurances,

  // Clinicians (user-clinics available clinicians)
  getAvailableCliniciansUserClinicsAvailableGet as getAvailableClinicians,
  listClinicPhysiciansAdminClinicPhysiciansGet as listClinicPhysicians,
  listAgencyClinicPhysiciansAgencyClinicsAgencyClinicIdPhysiciansGet as listAgencyClinicPhysicians,

  // Clinician Manager
  listOrgMembersUsersOrgMembersGet as listOrgMembers,
  exportOrgMembersUsersOrgMembersExportGet as exportOrgMembers,
  syncClinicAssignmentsUsersUserIdClinicAssignmentsPut as syncClinicAssignments,
  assignUserToAllClinicsUsersUserIdAssignAllClinicsPost as assignUserToAllClinics,
  inviteUserUsersInvitePost as inviteUser,
  onboardUsersEndpointUsersOnboardPost as onboardUsers,
  resendInviteUsersResendInvitePost as resendInvite,
  rescindInviteUsersRescindInvitePost as rescindInvite,
  deactivateUserUsersUserIdDeactivatePost as deactivateUser,
  retryDeactivateUserUsersUserIdDeactivateRetryPost as retryDeactivateUser,
  updateUserNpiUsersUserIdNpiPatch as updateUserNpi,
  updateUserTypeUsersUserIdUserTypePatch as updateUserType,
  updateUserCredentialsUsersUserIdCredentialsPatch as updateUserCredentials,
  reactivateAgencyMemberUsersUserIdReactivatePost as reactivateAgencyMember,
  listUserMergeCandidatesUsersMergeCandidatesGet as listUserMergeCandidates,
  previewUserMergeUsersMergePreviewGet as previewUserMerge,
  mergeUsersUsersMergePost as mergeUsers,
  retryUserMergeStepsUsersMergesMergeIdRetryPost as retryUserMergeSteps,

  // Agency Clinics
  getAgencyClinicsAgencyClinicsGet as getAgencyClinics,

  // Dashboard Clinics
  listDashboardClinics,

  // Agencies (current-agency update/delete; Internal Tools list/create below)
  updateCurrentAgencyAgenciesPut as updateAgency,
  updateCurrentAgencyWrittenNameAgenciesWrittenNamePut as updateAgencyWrittenName,
  deleteCurrentAgencyAgenciesDelete as deleteAgency,

  // Internal Tools — agencies / onboarding / cross-agency remove
  getAgenciesInternalToolsAgenciesGet as getAgencies,
  listAgenciesWithAssignedRolesInternalToolsAgenciesWithAssignedRolesGet as listAgenciesWithAssignedRoles,
  createAgencyInternalToolsAgenciesPost as createAgency,
  removeUserFromAgencyInternalToolsAgenciesAgencyIdUsersUserIdDelete as removeUserFromAgency,
  updateAgencyTimezoneInternalToolsAgenciesAgencyIdPatch as updateAgencyTimezone,
  getTemporalScalingOverridesInternalToolsTemporalScalingOverridesGet as getTemporalScalingOverrides,
  updateTemporalScalingOverrideInternalToolsTemporalScalingOverridesPut as updateTemporalScalingOverride,
  getOnboardingTeamConfigInternalToolsOnboardingTeamConfigGet as getOnboardingTeamConfig,
  updateOnboardingTeamConfigInternalToolsOnboardingTeamConfigPut as updateOnboardingTeamConfig,
  syncOnboardingTeamInternalToolsOnboardingTeamConfigSyncPost as syncOnboardingTeam,

  // Internal Tools — account merges
  listMergeCandidatesInternalToolsAgenciesAgencyIdUsersMergeCandidatesGet as listMergeCandidates,
  previewMergeInternalToolsAgenciesAgencyIdUsersMergePreviewGet as previewAccountMerge,
  mergeAccountsInternalToolsAgenciesAgencyIdUsersMergePost as mergeAccounts,
  listMergesInternalToolsAgenciesAgencyIdUsersMergesGet as listAccountMerges,
  retryMergeStepsInternalToolsAgenciesAgencyIdUsersMergesMergeIdRetryPost as retryAccountMergeSteps,
  reverseMergeInternalToolsAgenciesAgencyIdUsersMergesMergeIdReversePost as reverseAccountMerge,

  // Internal Tools — Stedi webhook replay (835 only)
  preflightStediReplayInternalToolsStediReplayPreflightPost as preflightStediReplay,
  replayStediWebhooksInternalToolsStediReplayPost as replayStediWebhooks,

  // Internal Tools: Telnyx fax lines
  listTelnyxFaxLinesInternalToolsTelnyxFaxLinesGet as listTelnyxFaxLines,
  getTelnyxFaxLineInternalToolsTelnyxFaxLinesAgencyIdGet as getTelnyxFaxLine,
  provisionTelnyxFaxLineInternalToolsTelnyxFaxLinesAgencyIdProvisionPost as provisionTelnyxFaxLine,
  setTelnyxOutboundNumberInternalToolsTelnyxFaxLinesAgencyIdOutboundNumberPut as setTelnyxOutboundNumber,
  addTelnyxNumberInternalToolsTelnyxFaxLinesAgencyIdNumbersPost as addTelnyxNumber,
  assignTelnyxNumbersInternalToolsTelnyxFaxLinesAgencyIdNumbersAssignPost as assignTelnyxNumbers,
  forgetTelnyxFaxApplicationInternalToolsTelnyxFaxLinesAgencyIdForgetApplicationPost as forgetTelnyxFaxApplication,

  // Demo agency setup (non-prod; Strawhat super-user)
  listDemoEnvironmentsDemoAgencySetupEnvironmentsGet as listDemoEnvironments,
  getDemoTeamDefaultDemoAgencySetupTeamDefaultGet as getDemoTeamDefault,
  setDemoTeamDefaultDemoAgencySetupTeamDefaultPut as setDemoTeamDefault,
  updateDemoEnvironmentDemoAgencySetupEnvironmentsAgencyIdPut as updateDemoEnvironment,
  runDemoEnvironmentDemoAgencySetupEnvironmentsAgencyIdRunPost as runDemoEnvironment,
  pullKinnserForDemoEnvironmentDemoAgencySetupEnvironmentsAgencyIdPullKinnserPost as pullKinnser,
  rescrapeKinnserForDemoEnvironmentDemoAgencySetupEnvironmentsAgencyIdRescrapeKinnserPost as rescrapeKinnser,
  getKinnserRescrapeStatusForDemoEnvironmentDemoAgencySetupEnvironmentsAgencyIdRescrapeKinnserGet as getKinnserRescrapeStatus,
  listDemoEnvironmentRunsDemoAgencySetupEnvironmentsAgencyIdRunsGet as listDemoEnvironmentRuns,
  listPulledPayersDemoAgencySetupEnvironmentsAgencyIdPulledPayersGet as listPulledPayers,

  // Internal Tools — one agency's Kinnser data (EMR-2141). Mounted in every
  // stage, unlike the demo routes above, and gated by the kinnser-agency-sync-ui
  // flag on the caller's own organization.
  listKinnserSyncAgenciesInternalToolsKinnserAgencySyncAgenciesGet as listKinnserSyncAgencies,
  listKinnserAgencyClinicsInternalToolsKinnserAgencySyncAgenciesAgencyIdClinicsGet as listKinnserAgencyClinics,
  pullKinnserDataInternalToolsKinnserAgencySyncAgenciesAgencyIdPullPost as pullKinnserData,
  writeKinnserClinicIdentityInternalToolsKinnserAgencySyncAgenciesAgencyIdClinicsClinicKeyIdentityPut as writeKinnserClinicIdentity,
  getKinnserSyncStatusInternalToolsKinnserAgencySyncAgenciesAgencyIdGet as getKinnserSyncStatus,
  setKinnserClinicLeftOutInternalToolsKinnserAgencySyncAgenciesAgencyIdClinicsClinicKeyLeftOutPut as setKinnserClinicLeftOut,

  // Users
  getUsersUsersGet as getUsers,
  getAgencyMembersUsersAgencyMembersGet as getAgencyMembers,
  getUserUsersUserIdGet as getUser,
  createUserUsersPost as createUser,
  updateUserUsersUserIdPut as updateUser,
  getUsersByPermissionUsersByPermissionGet as getUsersByPermission,
  updateUserRolesUsersUserIdRolesPut as updateUserRoles,
  updateClinicAccessUsersUserIdClinicAccessPut as updateClinicAccess,
  updateClinicSetupUsersUserIdClinicSetupPut as updateClinicSetup,

  // Staff licenses
  countUserLicensesUserLicensesCountsGet as countUserLicenses,

  // Goal sync workflow
  startSyncGoalsForAgencyWorkflowGoalsDashboardSyncGoalsForAgencyAsyncPost as startSyncGoalsForAgencyWorkflow,
  getSyncGoalsForAgencyWorkflowStatusGoalsDashboardSyncGoalsForAgencyStatusGet as getSyncGoalsForAgencyWorkflowStatus,
  // RBAC
  listPermissionsRbacPermissionsGet as listPermissions,
  getPermissionRbacPermissionsPermissionIdGet as getPermission,
  createPermissionRbacPermissionsPost as createPermission,
  updatePermissionRbacPermissionsPermissionIdPatch as updatePermission,
  listWorkosFeatureFlagsRbacFeatureFlagsGet as listWorkosFeatureFlags,
  listRolesRbacRolesGet as listRoles,
  getRoleRbacRolesRoleIdGet as getRole,
  createRoleRbacRolesPost as createRole,
  updateRoleRbacRolesRoleIdPatch as updateRole,
  deleteRoleRbacRolesRoleIdDelete as deleteRole,
  listRolePermissionsRbacRolePermissionsRoleIdPermissionsGet as listRolePermissions,
  addPermissionToRoleRbacRolePermissionsRoleIdPermissionsPost as addPermissionToRole,
  removePermissionFromRoleRbacRolePermissionsRoleIdPermissionsPermissionIdDelete as removePermissionFromRole,
  createAgencyAssignmentRbacRoleAssignmentsAgencyPost as createAgencyAssignment,
  createClinicAssignmentRbacRoleAssignmentsClinicPost as createClinicAssignment,
  bulkUpsertAssignmentsRbacRoleAssignmentsBulkPut as bulkUpsertAssignments,
  getUsersByRoleRbacRoleAssignmentsUsersByRoleRoleIdGet as getUsersByRole,
  bulkAssignAgencyRolesToUserRbacRoleAssignmentsUserUserIdAgencyRolesPut as bulkAssignAgencyRolesToUser,
  bulkAssignClinicRolesToUserRbacRoleAssignmentsUserUserIdClinicRolesPut as bulkAssignClinicRolesToUser,
  getAssignmentRbacRoleAssignmentsAssignmentIdGet as getAssignment,
  deleteAssignmentRbacRoleAssignmentsAssignmentIdDelete as deleteAssignment,
  listUserRoleAssignments,
  listDistinctRolesAssignedAtAgencyRbacRoleAssignmentsAgenciesAgencyIdAssignedRolesGet as listDistinctRolesAssignedAtAgency,

  // Eligibility
  performRealEligibilityCheckEligibilityIntakeIntakeIdCheckPost as checkEligibility,
  performPatientEligibilityCheckEligibilityPatientPatientIdCheckPost as checkPatientEligibility,
  getEligibilityChecksEndpointEligibilityGet as getEligibilityChecks,

  // Stedi
  checkEligibilityStediEligibilityPost as stediCheckEligibility,
  checkCoordinationOfBenefitsStediCoordinationOfBenefitsPost as stediCheckCoordinationOfBenefits,
  submitClaim837iStediClaim837iPost as stediSubmitClaim837i,
  getStediClaimUb04PdfStediClaimsClaimIdUb04Get as stediGetClaimUb04Pdf,

  // Waystar
  checkPriorAuthorizationWaystarPriorAuthPost as waystarCheckPriorAuthorization,
  checkClaimAcknowledgmentWaystarClaimsClaimIdAckGet as waystarCheckClaimAcknowledgment,

  // Authorization
  getIntakesForAuthManagementAuthorizationGet as getIntakesForAuth,

  // Medications
  searchMedicationsEndpointMedicationsGet as searchMedications,
  getMedicationEndpointMedicationsMedicationIdGet as getMedication,

  // Patient Medications
  getPatientMedicationsPatientsPatientIdMedicationsGet as getPatientMedications,
  createPatientMedicationPatientsPatientIdMedicationsPost as createPatientMedication,
  getPatientMedicationPatientsPatientIdMedicationsMedicationIdGet as getPatientMedication,
  updatePatientMedicationPatientsPatientIdMedicationsMedicationIdPut as updatePatientMedication,
  deletePatientMedicationPatientsPatientIdMedicationsMedicationIdDelete as deletePatientMedication,
  getMedicationPdfPatientsPatientIdMedicationsPdfPost as getMedicationPdf,
  printPatientMedicationsPatientsPatientIdMedicationsPrintGet as printPatientMedications,

  // Imported (raw Kinnser) medication lines. Deliberately not merged into
  // getPatientMedications: that array feeds CMS-485 prefill, the MAR and
  // interaction screening, none of which may see an uncoded line.
  getImportedMedicationLinesPatientsPatientIdMedicationsImportedGet as getImportedMedicationLines,
  reconcileImportedMedicationLine,
  declineImportedMedicationLine,
  screenMedicationInteractions,
  screenCandidateMedication,
  getMedicationOrderLaunchValidation,
  createMedicationOrder,

  // Medication Administration Records (MAR)
  listMarMedicationsPatientsPatientIdVisitsVisitIdMarGet as listMarMedications,
  createMarMedicationPatientsPatientIdVisitsVisitIdMarPost as createMarMedication,
  updateMarMedicationPatientsPatientIdVisitsVisitIdMarMarIdPut as updateMarMedication,
  deleteMarMedicationPatientsPatientIdVisitsVisitIdMarMarIdDelete as deleteMarMedication,

  // Medication Profile Attachments
  getMedicationProfileAttachmentsPatientsPatientIdMedicationsAttachmentsGet as getMedicationProfileAttachments,
  uploadMedicationProfileAttachmentPatientsPatientIdMedicationsAttachmentsPost as uploadMedicationProfileAttachment,
  getMedicationAttachmentUrlPatientsPatientIdMedicationsAttachmentsAttachmentIdUrlGet as getMedicationAttachmentUrl,
  deleteMedicationProfileAttachmentPatientsPatientIdMedicationsAttachmentsAttachmentIdDelete as deleteMedicationProfileAttachment,

  // Patient Pharmacy
  getPatientPharmacyPatientsPatientIdPharmacyGet as getPatientPharmacy,
  upsertPatientPharmacyPatientsPatientIdPharmacyPut as upsertPatientPharmacy,

  // Infection Logs
  listInfectionLogsPatientsPatientIdInfectionLogsGet as listInfectionLogs,
  createInfectionLogEndpointPatientsPatientIdInfectionLogsPost as createInfectionLog,
  getInfectionLogDetailPatientsPatientIdInfectionLogsInfectionLogIdGet as getInfectionLogDetail,
  updateInfectionLogEndpointPatientsPatientIdInfectionLogsInfectionLogIdPut as updateInfectionLog,
  deleteInfectionLogEndpointPatientsPatientIdInfectionLogsInfectionLogIdDelete as deleteInfectionLog,
  saveFollowUpPatientsPatientIdInfectionLogsInfectionLogIdFollowUpsPost as saveInfectionFollowUp,
  reScreenCovidPatientsPatientIdInfectionLogsInfectionLogIdReScreenPost as reScreenCovid,
  getInfectionLogPdfPatientsPatientIdInfectionLogsInfectionLogIdPdfGet as getInfectionLogPdf,

  // Episode vitals summary (EMR-2535)
  getEpisodeVitalsSummaryPatientsPatientIdEpisodesEpisodeIdVitalsSummaryGet as getEpisodeVitalsSummary,

  // Wounds
  getPatientWoundsPatientsPatientIdWoundsGet as getPatientWounds,
  getPatientWoundVisitActivityPatientsPatientIdWoundsVisitActivityGet as getPatientWoundVisitActivity,
  createPatientWoundPatientsPatientIdWoundsPost as createPatientWound,
  updatePatientWoundPatientsPatientIdWoundsWoundIdPut as updatePatientWound,
  deletePatientWoundPatientsPatientIdWoundsWoundIdDelete as deletePatientWound,

  // Wound Care Worksheet PDF
  getWoundCareWorksheetPdfPatientsPatientIdWoundsPdfPost as getWoundCareWorksheetPdf,

  // Wound Assessments
  getWoundAssessmentsPatientsPatientIdWoundsWoundIdAssessmentsGet as getWoundAssessments,
  createWoundAssessmentPatientsPatientIdWoundsWoundIdAssessmentsPost as createWoundAssessment,
  updateWoundAssessmentPatientsPatientIdWoundsWoundIdAssessmentsAssessmentIdPut as updateWoundAssessment,
  deleteWoundAssessmentPatientsPatientIdWoundsWoundIdAssessmentsAssessmentIdDelete as deleteWoundAssessment,

  // Wound Attachments
  getWoundAttachmentsPatientsPatientIdWoundsWoundIdAttachmentsGet as getWoundAttachments,
  uploadWoundAttachmentPatientsPatientIdWoundsWoundIdAttachmentsPost as uploadWoundAttachment,
  getWoundAttachmentUrlPatientsPatientIdWoundsWoundIdAttachmentsAttachmentIdUrlGet as getWoundAttachmentUrl,
  deleteWoundAttachmentPatientsPatientIdWoundsWoundIdAttachmentsAttachmentIdDelete as deleteWoundAttachment,

  // Retained historical claim evidence
  listHistoricalClaimCaptures,
  getHistoricalClaimCapture,
  downloadHistoricalClaimPrint,
  // Fall Logs
  listFallLogsPatientsPatientIdFallLogsGet as listFallLogs,
  createFallLogEndpointPatientsPatientIdFallLogsPost as createFallLog,
  updateFallLogEndpointPatientsPatientIdFallLogsFallLogIdPut as updateFallLog,
  deleteFallLogEndpointPatientsPatientIdFallLogsFallLogIdDelete as deleteFallLog,
  getFallLogPdfPatientsPatientIdFallLogsFallLogIdPdfGet as getFallLogPdf,

  // Episode Authorizations
  listEpisodeAuthorizationsEpisodesEpisodeIdAuthorizationsGet as listEpisodeAuthorizations,
  createEpisodeAuthorizationEpisodesEpisodeIdAuthorizationsPost as createEpisodeAuthorization,
  updateEpisodeAuthorizationEpisodesEpisodeIdAuthorizationsAuthorizationIdPut as updateEpisodeAuthorization,
  deleteEpisodeAuthorizationEpisodesEpisodeIdAuthorizationsAuthorizationIdDelete as deleteEpisodeAuthorization,
  setVisitAuthorizationVisitsVisitIdAuthorizationPatch as setVisitAuthorization,
  getAuthorizationErrorsEpisodesEpisodeIdAuthorizationsErrorsGet as getAuthorizationErrors,
  getAuthorizationEvaluationEpisodesEpisodeIdAuthorizationsEvaluationGet as getAuthorizationEvaluation,
  checkPlannedVisits,

  // Goals
  getPatientGoalsForVisitGoalsVisitVisitIdGet as getPatientGoalsForVisit,
  getPatientGoalForVisitGoalsVisitVisitIdGoalPatientGoalIdGet as getPatientGoalForVisit,
  getGoalOptionsForVisitGoalsVisitVisitIdOptionsGet as getGoalOptionsForVisit,
  getGoalTemplatesGoalsVisitVisitIdTemplateBatchPost as getGoalTemplatesForCreation,
  addGoalToVisitGoalsVisitVisitIdPost as addGoalToVisit,
  addKinnserGoalToEpisodeGoalsEpisodeEpisodeIdPost as addKinnserGoalToEpisode,
  getGoalOptionsForEpisodeGoalsEpisodeEpisodeIdOptionsGet as getGoalOptionsForEpisode,
  getGoalTemplatesForEpisodeGoalsEpisodeEpisodeIdTemplateBatchPost as getGoalTemplatesForEpisode,
  getGoalsToContinueGoalsEpisodeEpisodeIdCarryForwardGet as getGoalsToContinue,
  continueGoalsFromPreviousEpisodeGoalsEpisodeEpisodeIdCarryForwardPost as continueGoalsFromPreviousEpisode,
  recordGoalProgressGoalsVisitProgressVisitIdProgressPatientGoalIdRecordProgressPost as recordGoalProgress,
  markPatientGoalMetGoalsVisitProgressVisitIdProgressPatientGoalIdMarkMetPost as markPatientGoalMet,
  markPatientGoalUnattainableGoalsVisitProgressVisitIdProgressPatientGoalIdMarkUnattainablePost as markPatientGoalUnattainable,
  updateGoalForVisitGoalsVisitVisitIdPatientGoalIdPut as updateGoalForVisit,
  deleteGoalFromVisitGoalsVisitVisitIdPatientGoalIdDelete as deleteGoalFromVisit,
  acceptImportedGoalGoalsVisitVisitIdPatientGoalIdAcceptImportPost as acceptImportedGoal,
  extendGoalToNextEpisodeGoalsVisitProgressVisitIdGoalPatientGoalIdExtendPost as extendGoalToNextEpisode,
  // Returns the next non-cancelled episode (completed/interrupted included);
  // the generated name keeps the route's historical "next-active" segment.
  getNextActiveEpisodeEpisodesEpisodeIdNextActiveGet as getNextEpisodeForEpisode,
  // Interventions
  addInterventionToVisitGoalsVisitVisitIdInterventionPost as addInterventionToVisit,
  getInterventionOptionsForVisitGoalsVisitVisitIdInterventionOptionsGet as getInterventionOptionsForVisit,
  getInterventionsForPatientGoalGoalsVisitVisitIdInterventionPatientGoalIdGet as getInterventionsForPatientGoal,
  performInterventionForVisitGoalsVisitProgressVisitIdProgressPatientInterventionIdPerformPost as performInterventionForVisit,
  completeInterventionForVisitGoalsVisitProgressVisitIdProgressPatientInterventionIdCompletePost as completeInterventionForVisit,
  deleteInterventionFromVisitGoalsVisitVisitIdInterventionPatientInterventionIdDelete as deleteInterventionFromVisit,
  // Billing
  getDraftBillingCyclesApiBillingBillingCyclesDraftsPost as getDraftBillingCycles,
  downloadRemittanceRemittancesRemittanceIdDownloadGet as downloadRemittance,
  getRemittanceDetailRemittancesRemittanceIdGet as getRemittanceDetail,
  getRemittanceStatusCountsRemittancesStatusCountsGet as getRemittanceStatusCounts,
  listRemittancePayorsRemittancesPayorsGet as listRemittancePayors,
  listRemittancesRemittancesGet as listRemittances,
  uploadRemittanceRemittancesUploadPost as uploadRemittance,
  setBillingCycleStatusPendingApprovalBillingBillingCycleBillingCycleIdSetStatusPendingApprovalPost as setBillingCycleStatusPendingApproval,
  setBillingCycleStatusReadyToSendBillingBillingCycleBillingCycleIdSetStatusReadyToSendPost as setBillingCycleStatusReadyToSend,
  setBillingCyclesStatusVoidBillingBillingCyclesSetStatusVoidPost as setBillingCyclesStatusVoid,
  setBillingCycleStatusPendingPaymentBillingBillingCycleBillingCycleIdSetStatusPendingPaymentPost as setBillingCycleStatusPendingPayment,
  setBillingCycleStatusPaidBillingBillingCycleBillingCycleIdSetStatusPaidPost as setBillingCycleStatusPaid,
  reopenBillingCycleBillingBillingCycleBillingCycleIdSetStatusReopenPost as reopenBillingCycle,
  undoBillingCycleMarkedSentBillingBillingCycleBillingCycleIdSetStatusUndoSentPost as undoBillingCycleMarkedSent,
  undoBillingCycleMarkedPaidBillingBillingCycleBillingCycleIdSetStatusUndoPaidPost as undoBillingCycleMarkedPaid,
  setBillingCycleStatusRejectedBillingBillingCycleBillingCycleIdSetStatusRejectedPost as setBillingCycleStatusRejected,
  sendBillingCycleAgainBillingBillingCycleBillingCycleIdSendAgainPost as sendBillingCycleAgain,
  revertToReadyNoasNoaIdRevertToReadyPost as revertNoaToReady,
  getBillingCyclePayerResponseBillingBillingCycleBillingCycleIdPayerResponseGet as getBillingCyclePayerResponse,
  getNoaPayerResponseNoasNoaIdPayerResponseGet as getNoaPayerResponse,
  getNoaWorkLogNoasNoaIdWorkLogGet as getNoaWorkLog,
  sendBillingCycleToClearinghouseBillingBillingCycleBillingCycleIdSendToClearinghousePost as sendBillingCycleToClearinghouse,
  refreshBillingCyclesBillingRefreshBillingCyclesPost as refreshBillingCycles,
  getRefreshBillingCyclesStatusBillingRefreshBillingCyclesWorkflowIdStatusGet as getRefreshBillingCyclesStatus,
  getActiveBillingCyclesApiBillingBillingCyclesActiveCyclesPost as getActiveBillingCycles,
  getBillingCyclesMetaApiBillingBillingCyclesMetaGet as getBillingCyclesMeta,
  getBillingCycleEoePreviewApiBillingBillingCycleBillingCycleIdEoePreviewGet as getBillingCycleEoePreview,
  getBillingCycleReadinessDetails,
  getBillingCycleReadinessApiBillingBillingCycleBillingCycleIdReadinessGet as getBillingCycleReadiness,
  overridePaymentOasisHoldBillingBillingCycleBillingCycleIdPaymentOasisHoldOverridePost as overridePaymentOasisHold,
  getBillingCycleHippsPreviewApiBillingBillingCycleBillingCycleIdHippsPreviewGet as getBillingCycleHippsPreview,
  getBillingCycleVisitsAndSuppliesApiBillingBillingCycleBillingCycleIdVisitsAndSuppliesGet as getBillingCycleVisitsAndSupplies,
  updateBillingWorksheetApiBillingBillingCycleUpdateWorksheetPost as updateBillingWorksheet,
  getBillingCycleWorksheetBillingBillingCycleBillingCycleIdWorksheetGet as getBillingCycleWorksheet,
  getBillingClaimWorksheetBillingClaimsClaimIdWorksheetGet as getBillingClaimWorksheet,
  replaceBillingCycleWorksheetBillingBillingCycleBillingCycleIdWorksheetFullPut as replaceBillingCycleWorksheet,
  refreshBillingCycleWorksheetBillingBillingCycleBillingCycleIdWorksheetRefreshPost as refreshBillingCycleWorksheet,
  downloadBillingCycleUb04PdfBillingBillingCycleBillingCycleIdDownloadUb04PdfGet as downloadBillingCycleUb04Pdf,
  downloadBillingCycle837EdiBillingBillingCycleBillingCycleIdDownload837EdiGet as downloadBillingCycle837Edi,
  listBillingCycleNotesBillingBillingCycleBillingCycleIdNotesGet as listBillingCycleNotes,
  createBillingCycleNoteBillingBillingCycleBillingCycleIdNotesPost as createBillingCycleNote,
  getBillingCycleNoteBillingBillingCycleNotesNoteIdGet as getBillingCycleNote,
  updateBillingCycleNoteBillingBillingCycleNotesNoteIdPatch as updateBillingCycleNote,
  deleteBillingCycleNoteBillingBillingCycleNotesNoteIdDelete as deleteBillingCycleNote,
  // EMR-1409: claim-version replacement, timeline, and reconciliation attach
  createReplacementClaimBillingBillingCycleBillingCycleIdReplacementPost as createReplacementClaim,
  abandonReplacementClaimBillingBillingCycleBillingCycleIdReplacementAbandonPost as abandonReplacementClaim,
  getBillingCycleClaimTimelineBillingBillingCycleBillingCycleIdClaimTimelineGet as getBillingCycleClaimTimeline,
  reconciliationListUnmatched277CaClaimStatuses as listUnmatched277caClaimStatuses,
  reconciliationResolveClaimTransmission as resolveClaimTransmission,
  reconciliationAttach277CaClaimStatus as attach277caClaimStatus,
  reconciliationAttach835RemittancePayment as attach835RemittancePayment,
  // EMR-1409: version-grained claim search for remittance reconciliation
  searchRemittanceClaimsRemittancesClaimsSearchGet as searchRemittanceClaims,
  // Task Documents
  getTaskDocumentsTasksTaskIdDocumentsGet as getTaskDocuments,
  uploadTaskDocumentTasksTaskIdDocumentsPost as uploadTaskDocument,
  getTaskDocumentUrlTasksTaskIdDocumentsDocumentIdUrlGet as getTaskDocumentUrl,
  deleteTaskDocumentTasksTaskIdDocumentsDocumentIdDelete as deleteTaskDocument,
  getTaskDetailsTasksTaskIdDetailsGet as getTaskDetails,
  updateTaskDetailsNotesTasksTaskIdDetailsNotesPut as updateTaskDetailsNotes,
  updateTaskDetailsVisitFieldsTasksTaskIdDetailsVisitFieldsPut as updateTaskDetailsVisitFields,
  uploadTaskDetailsDocumentTasksTaskIdDetailsDocumentsPost as uploadTaskDetailsDocument,
  deleteTaskDetailsDocumentTasksTaskIdDetailsDocumentsDocumentIdDelete as deleteTaskDetailsDocument,

  // Task restore (undelete), one route for every task subtype
  restoreTaskEndpointTasksTaskIdRestorePost as restoreTask,

  // Product updates (public modal + Internal Tools management)
  getProductUpdatesForUserProductUpdatesGet as getProductUpdatesForUser,
  getAllProductUpdatesInternalToolsProductUpdatesAllGet as listAllProductUpdates,
  updateProductUpdateEntryInternalToolsProductUpdatesManagementPatch as upsertProductUpdate,
  deleteProductUpdateEntryInternalToolsProductUpdatesManagementProductUpdateIdDelete as deleteProductUpdate,
  uploadProductUpdateImageInternalToolsProductUpdatesManagementImagesPost as uploadProductUpdateImage,
  deleteProductUpdateImageInternalToolsProductUpdatesManagementImagesImageIdDelete as deleteProductUpdateImage,
} from './api/sdk.gen';

// Chat (EMR-1875) and group-scoped role assignments (EMR-1876)
export {
  getInboxChatInboxGet as getChatInbox,
  getChannelChatChannelsChannelIdGet as getChatChannel,
  getMessagesChatChannelsChannelIdMessagesGet as getChatMessages,
  sendMessageChatChannelsChannelIdMessagesPost as sendChatMessage,
  setMessageReactionChatChannelsChannelIdMessagesMessageIdReactionsPut as setChatMessageReaction,
  editMessageChatChannelsChannelIdMessagesMessageIdPatch as editChatMessage,
  deleteMessageChatChannelsChannelIdMessagesMessageIdDelete as deleteChatMessage,
  markReadChatChannelsChannelIdReadPost as markChatChannelRead,
  startConversationChatConversationsPost as startChatConversation,
  getDirectoryChatDirectoryGet as getChatDirectory,
  searchChannelMembersChatChannelsChannelIdMembersGet as searchChatChannelMembers,
  requestAttachmentUploadChatChannelsChannelIdAttachmentsPost as requestChatAttachmentUpload,
  issueAttachmentUrlsChatAttachmentsUrlsPost as getChatAttachmentUrls,
  downloadAttachmentChatAttachmentsAttachmentIdDownloadGet as getChatAttachmentDownload,
  createGroupAssignmentRbacRoleAssignmentsGroupPost as createGroupAssignment,
  listGroupAssignmentsRbacRoleAssignmentsGroupGroupIdGet as listGroupAssignments,
  getChannelByEpisodeChatChannelsByEpisodeEpisodeIdGet,
  openChannelByEpisodeChatChannelsByEpisodeEpisodeIdPost,
  postLinkTitlesChatLinkTitlesPost as resolveChatLinkTitles,
} from './api/sdk.gen';

export { getBillingCycleCountForPatientApiBillingBillingCyclesCountGet as getPatientBillingCycleCount } from './api/sdk.gen';

export { updateImporterSettingsImporterSettingsPut as updateImporterSettings } from './api/sdk.gen';

export { listPatientDuplicateImportsImporterPatientsPatientIdDuplicateImportsGet as listPatientDuplicateImports } from './api/sdk.gen';

export {
  autoAddEpisode,
  autoAddAdmissionAndEpisode,
  createTaskForEpisode,
  taskUploadForEpisode,
  updateTaskDetails,
  TAB_ID_TO_TAB_NAME,
  TAB_NAME_TO_TAB_ID,
} from './kinnser';

export type {
  AutoAddEpisodeRequest,
  AutoAddAdmissionAndEpisodeRequest,
  AutoAddAdmissionAndEpisodeResponse,
  EpisodeCreationResult,
  EpisodeLupaResponse,
  EpisodeLupaPeriodResponse,
  TaskUploadForEpisodeRequest,
  TaskUploadForEpisodeResponse,
  TaskDetailsUpdate,
  TaskDetailsUpdateComment,
  UpdateTaskDetailsRequest,
  UpdateTaskDetailsResponse,
  ScheduleInKinnserRequest,
  ScheduleInKinnserResponse,
  AutoAddInKinnserRequest,
  AutoAddInKinnserResponse,
  InboxRow,
  ChannelHeader,
  ChannelMemberOption,
  GroupAssignmentResponse,
  MessageOut,
  AttachmentOut,
  DirectoryRow,
  LinkTitleOut,
} from './api/types.gen';

export type { TaskCreateTabName, TaskUploadTabName } from './kinnser';

// Export commonly used types with better names
export type {
  IntakeResponse,
  IntakeListResponse,
  PatientResponse,
  VisitResponse,
  OrderResponse,
  OrderManagerItemResponse,
  PayorResponse,
  AgencyResponse,
  UserResponse,
  EligibilityCheckResponse,
  OtherAgencyPeriodOfCare,
  AvailableClinicianResponse as AvailableClinician,
  ClinicPhysicianResponse,
  PatientCreate as PatientCreateRequest,
  PatientUpdate as PatientUpdateRequest,
  MedicationResponse,
  FdbMedicationSearchResponse,
  FdbMedicationResult,
  FacetOption,
  InteractionScreen,
  DdiFinding,
  DaFinding,
  NotScreenedMedication,
  NotScreenedAllergy,
  ObsoleteMedication,
  FdbAllergenResult,
  FdbAllergenSearchResponse,
  PatientAllergyResponse,
  PatientAllergyCreateRequest,
  // Imported (raw Kinnser) chart lines — distinct types from the coded
  // responses above, which is what keeps an uncoded line out of the arrays
  // CMS-485 prefill, the MAR and interaction screening read.
  ImportedMedicationLineResponse,
  ImportedMedicationProfileResponse,
  ImportedAllergyLineResponse,
  ImportedAllergySuggestionResponse,
  ImportedMedicationSuggestionResponse,
  ImportedAllergyProfileResponse,
  PatientMedicationResponse,
  PatientMedicationListItem,
  PatientMedicationCreateWeb,
  PatientMedicationUpdate,
  MarMedicationResponse,
  MarMedicationCreate,
  MarMedicationUpdate,
  MedicationProfileAttachmentResponse,
  PatientPharmacyResponse,
  PatientPharmacyUpdate,
  PatientCommunicationResponse,
  PatientCommunicationCreate,
  PatientCommunicationUpdate,
  MiscTaskResponse,
  MiscTaskCreate,
  MiscTaskUpdate,
  OrgMemberResponse,
  ClinicAssignmentInfo,
  ClinicAssignmentUpdate,
  SyncClinicAssignmentsRequest,
  AssignAllClinicsRequest,
  RoleInfo,
  InviteUserRequest,
  InviteUserResponse,
  OnboardUsersRequest,
  OnboardUsersResponse,
  OnboardUserRequest,
  OnboardUserResult,
  OnboardStepStatus,
  OnboardRogerDocMode,
  UserArchetype,
  DisciplineType,
  TaskListResponse,
  TaskSignatureResponse,
  TaskSignatureResponse as VisitSignatureResponse,
  InfectionLogSummaryResponse,
  InfectionLogDetailResponse,
  InfectionLogCreate,
  InfectionLogUpdate,
  InfectionFollowUpCreate,
  InfectionFollowUpNoteResponse,
  InfectionCovidScreeningCreate,
  InfectionCovidScreeningResponse,
  InfectionType,
  InfectionStatus,
  InfectionSite,
  DiscoveryLocation,
  WoundResponse,
  WoundCreate,
  WoundUpdate,
  WoundAssessmentResponse,
  WoundAssessmentCreate,
  WoundAssessmentUpdate,
  WoundAttachmentResponse,
  FallLogResponse,
  FallLogCreate,
  FallLogUpdate,
  AuthorizationResponse,
  AuthorizationCreate,
  AuthorizationUpdate,
  AuthorizationStatus,
  AuthorizationError,
  AuthorizationErrorsResponse,
  AuthorizationEvaluationResponse,
  FallInjuryLevel,
  NatureOfFall,
  WitnessRelationship,
  YesNoNa,
  YesNoUnknown,
  AgencyGoalSyncStatusResponse,
  RoleAssignmentBulkUpsert,
  RoleAssignmentResponse,
  RoleResponse,
  UserAgencyRoleBulkAssign,
  UserClinicRoleAssignmentItem,
  UserClinicRoleBulkAssign,
  AgencyClinicResponse,
  DashboardClinicOption,
  ProductUpdateData,
  ProductUpdatesListResponse,
  ProductUpdateImageResponse,
  ProductUpdateManagementResponse,
  ProductUpdatesManagementListResponse,
  ProductUpdateUpdate,
  BillingCycleNoteResponse,
  BillingCycleNoteCreate,
  BillingCycleNoteUpdate,
  GoalLength,
  NextEpisodeResponse,
  EpisodicClaimStatus,
  ClinicDisciplineAssignmentItem,
  EmployeeTypeAssignmentItem,
  OnboardingTeamConfigResponse,
  OnboardingTeamConfigUpdate,
  OnboardingSyncResult,
  AgencyConfigUpdate,
  OnboardingDashboardData,
  OnboardingMetric,
  OnboardingMetricGroup,
  OnboardingTask,
} from './api/types.gen';

// Helper function for safe data extraction
export const safeExtract = <T>(
  response: { data?: T } | T,
  defaultValue: T
): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data?: T }).data || defaultValue;
  }
  return (response as T) || defaultValue;
};

// Helper function for file uploads
export const createFormData = (
  file: File,
  metadata?: Record<string, string | number | boolean>
): FormData => {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
  }
  return formData;
};
